// PostHog analytics + error tracking for the Hey Tasky frontend.
// Initialised once in main.tsx; consumed everywhere via this module.
import posthog from 'posthog-js'

const POSTHOG_KEY = import.meta.env?.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = import.meta.env?.VITE_POSTHOG_HOST as string | undefined

/** Call once at app boot (main.tsx). Safe to call without a key (becomes a no-op). */
export function initPostHog() {
  if (!POSTHOG_KEY) return

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST || 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,

    // Exception autocapture: unhandled errors + promise rejections.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },

    // Session replay so errors link to user recordings. Recording must also be
    // enabled for the project in PostHog → Settings → Session Replay.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
      recordCrossOriginIframes: true,
    },

    // Respect Do Not Track.
    respect_dnt: true,
  })
}

/** Identify the signed-in user (call after Clerk session loads). */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.identify(userId, traits)
}

/** Reset identity on sign-out. */
export function resetUser() {
  if (!POSTHOG_KEY) return
  posthog.reset()
}

/** Track a named product event. */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture(event, properties)
}

export { posthog }
